import { useT } from "@transifex/react";
import { useState } from "react";
import PhaseSixIcon from "../../../basic/phaseSixIcon/PhaseSixIcon";
import BasePopupWrapper, { TitleColorVariants } from "../../../basic/popups/BasePopupWrapper";
import type { ClassMember } from "../../../networking/schoolClasses";

interface ClassMemberViewPopupProps {
    isOpen: boolean;
    onClose: () => void;
    classMembers: ClassMember[];
    /** Loading the students failed: show that instead of claiming the class is empty. */
    hasLoadError: boolean;
    onSelectClassMember: (classMember: ClassMember) => void;
    /** Empty class: the popup offers adding students instead of an empty selection list. */
    onAddClassMembers: () => void;
}

const INITIAL_ITEMS_SHOWN = 4;

const ClassMemberViewPopup = (props: ClassMemberViewPopupProps) => {
    const t = useT();

    const [showAll, setShowAll] = useState(false);

    const t_title = t("Student view", { _tags: "SchoolClasses, ClassMemberViewPopup" });
    const t_question = t("From whose perspective would you like to enter the classroom?", {
        _tags: "SchoolClasses, ClassMemberViewPopup",
    });
    const t_showMore = t("show more", { _tags: "SchoolClasses, ClassMemberViewPopup" });
    const t_showLess = t("show less", { _tags: "SchoolClasses, ClassMemberViewPopup" });
    const t_cancel = t("Cancel", { _tags: "SchoolClasses, ClassMemberViewPopup" });
    const t_noStudentsYet = t(
        "This class has no students yet. Add students to enter the classroom from their perspective.",
        {
            _tags: "SchoolClasses, ClassMemberViewPopup",
        }
    );
    const t_addStudents = t("Add students", { _tags: "SchoolClasses, ClassMemberViewPopup" });
    const t_loadError = t("Could not load students. Please try again later.", {
        _tags: "SchoolClasses, ClassMemberViewPopup",
    });

    const hasClassMembers = props.classMembers.length > 0;
    const visibleClassMembers = showAll ? props.classMembers : props.classMembers.slice(0, INITIAL_ITEMS_SHOWN);
    const hasMoreClassMembers = props.classMembers.length > INITIAL_ITEMS_SHOWN;

    // After a failed load we don't know whether the class is empty, so don't offer adding students.
    const showAddClassMembers = !hasClassMembers && !props.hasLoadError;

    const renderClassMemberRow = (classMember: ClassMember) => (
        <button
            key={classMember.id}
            type="button"
            onClick={() => props.onSelectClassMember(classMember)}
            className="w-full flex items-center justify-between gap-m border border-p6-grey-mid rounded-medium pl-m pr-s py-s bg-white text-left cursor-pointer hover:bg-p6-grey-lightest"
        >
            <span className="text-l text-p6-grey-dark break-words">{classMember.name}</span>
            <PhaseSixIcon
                name="chevron-right"
                className="text-header-icon text-p6-grey-dark shrink-0"
            />
        </button>
    );

    const renderShowMoreToggle = () => {
        if (!hasMoreClassMembers) return null;

        return (
            <button
                type="button"
                aria-expanded={showAll}
                onClick={() => setShowAll((prev) => !prev)}
                className="flex items-center justify-center gap-xs self-center text-l text-p6-grey-dark cursor-pointer"
            >
                <span>{showAll ? t_showLess : t_showMore}</span>
                <PhaseSixIcon
                    name="chevron-down"
                    className={`text-header-icon transition-transform ${showAll ? "rotate-180" : ""}`}
                />
            </button>
        );
    };

    const renderClassMemberSelection = () => (
        <div className="flex flex-col gap-m">
            <p className="text-l text-p6-grey-dark">{t_question}</p>
            <div className="flex flex-col gap-s">
                {visibleClassMembers.map(renderClassMemberRow)}
                {renderShowMoreToggle()}
            </div>
        </div>
    );

    const renderBody = () => {
        if (hasClassMembers) return renderClassMemberSelection();

        return <p className="text-l text-p6-grey-dark">{props.hasLoadError ? t_loadError : t_noStudentsYet}</p>;
    };

    return (
        <BasePopupWrapper
            isOpen={props.isOpen}
            onClose={props.onClose}
            title={t_title}
            titleColor={TitleColorVariants.DEFAULT}
            secondaryButton={{
                text: t_cancel,
                onClick: props.onClose,
            }}
            primaryButton={
                showAddClassMembers ? { text: t_addStudents, icon: "add", onClick: props.onAddClassMembers } : undefined
            }
        >
            {renderBody()}
        </BasePopupWrapper>
    );
};

export default ClassMemberViewPopup;
